import './AuthLayout.css'

import type { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { DossLogomark } from '~/common/components/DossLogomark'

export type AuthLayoutType =
  | 'login'
  | 'forgot-password'
  | 'reset-password'
  | 'accept-invitation'
  | 'email-code'

interface AuthLayoutProps {
  type: AuthLayoutType
  /** Omit where a banner carries the whole message, as on an invalid invitation link */
  title?: string | undefined
  subtitle?: string | undefined
  children: ReactNode
}

/** Which surfaces offer a way back to sign-in */
const BACK_TO_LOGIN_TYPES: ReadonlySet<AuthLayoutType> = new Set([
  'forgot-password',
  'reset-password',
])

/** Shared frame for the authentication surfaces: logomark over a centered single column */
export const AuthLayout = ({ type, title, subtitle, children }: AuthLayoutProps): ReactElement => {
  const { t } = useTranslation()

  return (
    <div className="auth-layout" data-surface={type}>
      <div className="content">
        <DossLogomark className="logomark" />

        {(title !== undefined || subtitle !== undefined) && (
          <div className="heading">
            {title !== undefined && <h2 className="title">{title}</h2>}
            {subtitle !== undefined && <p className="subtitle">{subtitle}</p>}
          </div>
        )}

        {children}

        {BACK_TO_LOGIN_TYPES.has(type) && (
          <div className="back-link">
            <Link to="/auth/login">{t('auth.back_to_login')}</Link>
          </div>
        )}
      </div>
    </div>
  )
}
